import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Send, TrendingUp, AlertOctagon, Activity, Cpu } from 'lucide-react';

const SignalsPage = ({ userRole, userName }) => {
    const [isPublishing, setIsPublishing] = useState(false);
    const [formData, setFormData] = useState({
        assetClass: 'Majors',
        assetName: 'EURUSD',
        direction: 'Buy',
        type: 'Intraday',
        risk: 'Medium',
        // --- NEW: AI Tracking Elements ---
        lifecycle_status: 'PENDING',
        structural_bias: 'BULLISH',
        // --- RESTORED: Exact Pricing ---
        entry: '',
        sl: '',
        tp1: '',
        tp2: '',
        tp3: '',
        ai_analysis_string: ''
    });

    const handlePublish = async (e) => {
        e.preventDefault();
        
        const cleanName = formData.assetName.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (cleanName === 'USDJPY' || cleanName === 'JPYUSD') {
            alert('Error: Trade ideas for USDJPY and JPYUSD are currently disabled and cannot be published to the CRM.');
            return;
        }

        setIsPublishing(true);

        try {
            const chartColor = formData.direction === 'Buy' ? 'bg-green-500/20' : 'bg-red-500/20';

            const { error } = await supabase.from('crm_signals').insert([{
                asset_class: formData.assetClass,
                asset_name: formData.assetName.toUpperCase(),
                direction: formData.direction,
                type: formData.type,
                risk: formData.risk,
                // --- NEW: AI Payload Integration ---
                lifecycle_status: formData.lifecycle_status,
                structural_bias: formData.structural_bias,
                ai_analysis_string: formData.ai_analysis_string,
                // --- RESTORED: Exact Target Data ---
                entry: formData.entry,
                sl: formData.sl,
                tp1: formData.tp1,
                tp2: formData.tp2 || null,
                tp3: formData.tp3 || null,
                author: userName || 'Nommia Analyst', 
                chart_color: chartColor,
                avatar: formData.assetClass === 'Crypto' ? 'BTC' : 'FX'
            }]);

            if (error) throw error;

            alert('Signal successfully published to the live trading platform!');
            
            // Reset fields
            setFormData({ ...formData, entry: '', sl: '', tp1: '', tp2: '', tp3: '', ai_analysis_string: '' });

        } catch (err) {
            console.error("Publish Error:", err);
            alert("Failed to publish signal. Please check your connection.");
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <div className="p-4 md:p-8 animate-fade-in flex flex-col h-full overflow-y-auto">
            <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                    <Activity className="text-amber-500" size={28} />
                    Trading Desk Signals
                </h2>
                <p className="text-slate-400 mt-1">Publish live market analysis directly to the client trading terminals.</p>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 max-w-4xl shadow-xl">
                <div className="flex items-center gap-3 mb-8 border-b border-slate-800 pb-4">
                    <TrendingUp className="text-amber-500" size={24} />
                    <div>
                        <h3 className="text-xl font-bold text-white">Create New Signal</h3>
                        <p className="text-xs text-slate-500">All fields marked with * are required.</p>
                    </div>
                </div>

                <form onSubmit={handlePublish} className="space-y-6">
                    
                    {/* Row 1: Core Trade Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase">Asset Name *</label>
                            <input required placeholder="e.g. EURUSD" className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white outline-none focus:border-amber-500 transition-colors" 
                                value={formData.assetName} onChange={e => setFormData({...formData, assetName: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase">Class *</label>
                            <select className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white outline-none focus:border-amber-500" 
                                value={formData.assetClass} onChange={e => setFormData({...formData, assetClass: e.target.value})}>
                                <option>Majors</option><option>Minors</option><option>Crypto</option><option>Indices</option><option>Commodities</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase">Direction *</label>
                            <select className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 outline-none font-bold text-white focus:border-amber-500" 
                                value={formData.direction} onChange={e => setFormData({...formData, direction: e.target.value})}>
                                <option value="Buy">Buy (Long)</option>
                                <option value="Sell">Sell (Short)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase">Risk *</label>
                            <select className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white outline-none focus:border-amber-500" 
                                value={formData.risk} onChange={e => setFormData({...formData, risk: e.target.value})}>
                                <option>Low</option><option>Medium</option><option>High</option>
                            </select>
                        </div>
                    </div>

                    {/* Row 2: AI State Tracking */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-800/30 p-4 rounded-xl border border-slate-700/50">
                        <div>
                            <label className="block text-xs font-bold text-blue-400 mb-2 uppercase flex items-center gap-1"><Cpu size={12}/> Lifecycle Status *</label>
                            <select className="w-full bg-slate-900 border border-blue-500/30 rounded-lg p-3 text-white font-bold outline-none focus:border-blue-500" 
                                value={formData.lifecycle_status} onChange={e => setFormData({...formData, lifecycle_status: e.target.value})}>
                                <option value="PENDING">PENDING (Setting up)</option>
                                <option value="ACTIVE">ACTIVE (Triggered)</option>
                                <option value="ADJUSTED">ADJUSTED (Market Shift)</option>
                                <option value="INVALIDATED">INVALIDATED (Expired/Failed)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-blue-400 mb-2 uppercase">Structural Bias *</label>
                            <input required className="w-full bg-slate-900 border border-blue-500/30 rounded-lg p-3 text-white outline-none focus:border-blue-500" 
                                placeholder="e.g. BULLISH"
                                value={formData.structural_bias} onChange={e => setFormData({...formData, structural_bias: e.target.value})} />
                        </div>
                    </div>

                    {/* Row 3: Exact Pricing Targets */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 bg-slate-950 p-6 rounded-xl border border-slate-800">
                        <div>
                            <label className="block text-xs font-bold text-blue-400 mb-2">ENTRY *</label>
                            <input required className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white font-mono text-sm outline-none focus:border-blue-500" 
                                value={formData.entry} onChange={e => setFormData({...formData, entry: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-red-400 mb-2">STOP LOSS *</label>
                            <input required className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white font-mono text-sm outline-none focus:border-red-500" 
                                value={formData.sl} onChange={e => setFormData({...formData, sl: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-green-400 mb-2">TARGET 1 *</label>
                            <input required className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white font-mono text-sm outline-none focus:border-green-500" 
                                value={formData.tp1} onChange={e => setFormData({...formData, tp1: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-green-500 mb-2">TARGET 2</label>
                            <input className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white font-mono text-sm outline-none focus:border-green-500" 
                                value={formData.tp2} onChange={e => setFormData({...formData, tp2: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-green-600 mb-2">TARGET 3</label>
                            <input className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white font-mono text-sm outline-none focus:border-green-500" 
                                value={formData.tp3} onChange={e => setFormData({...formData, tp3: e.target.value})} />
                        </div>
                    </div>

                    {/* Row 4: AI Analysis */}
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-2 uppercase flex items-center gap-1"><AlertOctagon size={14}/> AI Analysis / Rationale *</label>
                        <textarea required rows="4" className="w-full bg-slate-800 border border-slate-700 rounded-lg p-4 text-white outline-none focus:border-amber-500 text-sm" 
                            value={formData.ai_analysis_string} onChange={e => setFormData({...formData, ai_analysis_string: e.target.value})} 
                            placeholder="e.g. Price rejected off the daily order block..."></textarea>
                    </div>

                    <div className="pt-4 border-t border-slate-800">
                        <button type="submit" disabled={isPublishing} className="w-full md:w-auto px-8 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                            <Send size={18} /> {isPublishing ? 'Broadcasting to Terminals...' : 'Broadcast Signal Live'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SignalsPage;